import './BraceOrderDetails.scss';

import React from 'react';
import { formatDate } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import braceImageSrc from '@assets/images/brace.png';
import { ReactComponent as BracePendingImage } from '@assets/images/brace-pending.svg';
import Badge from '@components/Badge';
import { BadgeStyle } from '@components/Badge/Badge';
import Button, { ButtonStyle } from '@components/Button';
import DescriptionList from '@components/DescriptionList';
import FileUploaded from '@components/FileUploaded';
import { defaultDateFormat } from '@constants/general';
import { RoutePath } from '@constants/route-path';
import { BraceOrderStatus } from '@modulate/shared/src/constants/brace-order-status';
import { IBraceOrder } from '@modulate/shared/src/models/brace-order';

interface IProps {
  order: IBraceOrder;
}

function BraceOrderDetails({ order }: IProps) {
  const { t } = useTranslation();
  const getBadgeStyle = (status: BraceOrderStatus): { style: BadgeStyle; iconClassName?: string } => {
    switch (status) {
      case BraceOrderStatus.TwinReady:
        return { style: BadgeStyle.Purple };
      case BraceOrderStatus.Delivered:
        return { style: BadgeStyle.Green };
      case BraceOrderStatus.Pending:
        return { style: BadgeStyle.Grey /*, iconClassName: 'modIcon modIcon--loader'*/ };
      default:
        return { style: BadgeStyle.Black };
    }
  };

  const badgeStyle = getBadgeStyle(order.status);
  return (
    <article className="modBraceDetails">
      <div className="modBraceDetails__preview">
        <Badge style={badgeStyle.style}>
          {badgeStyle.iconClassName && <i className={badgeStyle.iconClassName} />}{' '}
          {t('enums.braceOrderStatus', { context: order.status })}
        </Badge>
        <div className="modBraceDetails__preview__image-container">
          {order.status === BraceOrderStatus.Pending && (
            <BracePendingImage className="modBraceDetailsPreviewImage" />
          )}
          {order.status === BraceOrderStatus.TwinReady && (
            <img className="modBraceDetailsPreviewImage" alt="Brace" src={braceImageSrc} />
          )}
        </div>
        {order.status === BraceOrderStatus.TwinReady && (
          <>
            <Button
              type="link"
              style={ButtonStyle.Primary}
              fullWidth
              to={generatePath(RoutePath.PaymentPage, {
                patientId: order.patientInfo.patientId,
                braceId: order.id,
              })}>
              {t('braceOrderDetails.payAndPrint')}
            </Button>
            <Button
              type="link"
              style={ButtonStyle.Action}
              fullWidth
              to={generatePath(RoutePath.SimulationReportPage, {
                patientId: order.patientInfo.patientId,
                braceId: order.id,
              })}>
              {t('braceOrderDetails.simulationReport')}
            </Button>
          </>
        )}
      </div>
      <div className="modBraceDetails__infos">
        <h4 className="modTitle modTitle--h4">{order.braceInfo.name}</h4>
        <DescriptionList
          items={[
            {
              title: t('braceOrderDetails.creationDate'),
              value: formatDate(order.braceInfo.date, defaultDateFormat),
            },
            {
              title: t('braceOrderDetails.hoursPerDay'),
              value: order.braceInfo.hoursPerDay.toString(),
            },
            {
              title: t('braceOrderDetails.type'),
              value: t('enums.braceType', { context: order.braceInfo.type }),
            },
            {
              title: t('braceOrderDetails.closure'),
              value: t('enums.openingType', { context: order.braceInfo.opening }),
            },
          ]}
        />

        <section className="modBraceDetails__infos__patient">
          <h4 className="modTitle modTitle--h5">{t('braceOrderDetails.patientInfo')}</h4>
          <DescriptionList
            items={[
              {
                title: t('braceOrderDetails.patientHeight'),
                value: `${order.patientInfo.height.toString()} cm`,
              },
              {
                title: t('braceOrderDetails.patientWeight'),
                value: `${order.patientInfo.weight.toString()} kg`,
              },
              {
                title: t('braceOrderDetails.prescription'),
                value: t('braceOrderDetails.prescriberInfo', {
                  name: order.prescriberName,
                  clinic: order.clinicName,
                }),
              },
            ]}
          />
          <div className="modBraceDetailsFiles">
            {order.prescriptionFiles.map((file) => (
              <FileUploaded key={file.id} file={file} small readonly />
            ))}
          </div>
          <div className="modBraceDetailsFiles">
            <p className="modText modText--body1">{t('braceOrderDetails.radiography')}</p>
            {order.radiographyFiles.map((file) => (
              <FileUploaded key={file.id} file={file} small readonly />
            ))}
          </div>
        </section>
      </div>
    </article>
  );
}

export default BraceOrderDetails;
